import { ensurePath, uploadOne } from './api/uploads.ts';

/**
 * A whole folder uploaded at once (143; Arthur, 21 October, 13:45): every file with the path of folders it sits in,
 * from a folder chosen with the picker or dropped on a folder. Procura makes those folders (ensure-path) and uploads
 * each file into its own; each file is scanned before it opens, with the same size limit as one file. The answer is a
 * count: uploaded, failed (each with why), and skipped (an operating system's own clutter, never a document).
 */
export interface PickedFile { file: File; path: string[] }
export interface UploadResult {
  uploaded: number;
  failed: { name: string; why: string }[];
  skipped: number;
}

/** Files an operating system leaves in folders, never a document: skipped, and counted. */
const CLUTTER = new Set(['.ds_store', 'thumbs.db', 'desktop.ini']);
export const isClutter = (name: string) => CLUTTER.has(name.toLowerCase()) || name.startsWith('._');

/** From an <input webkitdirectory>: each file's folders, the chosen folder's own name first. */
export function fromPicker(list: FileList): PickedFile[] {
  return Array.from(list).map((file) => {
    const parts = (file.webkitRelativePath || file.name).split('/').filter((p) => p !== '');
    return { file, path: parts.slice(0, -1) };
  });
}

/** From a drop: folders walked through the entry API (a folder's entries come in batches until an empty one). */
export async function fromDrop(items: DataTransferItemList): Promise<PickedFile[]> {
  const entries = Array.from(items).map((i) => i.webkitGetAsEntry?.()).filter((e): e is FileSystemEntry => !!e);
  const out: PickedFile[] = [];
  const walk = async (entry: FileSystemEntry, path: string[]): Promise<void> => {
    if (entry.isFile) {
      const file = await new Promise<File>((ok, no) => (entry as FileSystemFileEntry).file(ok, no));
      out.push({ file, path });
      return;
    }
    const reader = (entry as FileSystemDirectoryEntry).createReader();
    for (;;) {
      const batch = await new Promise<FileSystemEntry[]>((ok, no) => reader.readEntries(ok, no));
      if (batch.length === 0) break;
      for (const e of batch) await walk(e, [...path, entry.name]);
    }
  };
  for (const e of entries) await walk(e, []);
  return out;
}

/**
 * Uploads them into `folderId`, three at a time, making each path's folders once (the same path asked for once, its
 * answer kept). `onProgress` hears how many are done. A file too large or empty is refused here, before sending; the
 * server refuses it too.
 */
export async function uploadAll(
  getToken: () => Promise<string>, clientId: string, folderId: string, picked: PickedFile[], limitBytes: number,
  onProgress: (done: number, total: number) => void,
): Promise<UploadResult> {
  const result: UploadResult = { uploaded: 0, failed: [], skipped: 0 };
  const work = picked.filter((p) => { if (isClutter(p.file.name)) { result.skipped++; return false; } return true; });
  const folders = new Map<string, Promise<string>>();
  const folderFor = (path: string[]) => {
    const k = JSON.stringify(path.map((p) => p.trim().toLowerCase()));
    if (!folders.has(k)) {
      const p = path.length === 0 ? Promise.resolve(folderId) : ensurePath(getToken, clientId, folderId, path).then((r) => r.id);
      // A refused path is remembered as refused; every file under it fails with the same words.
      folders.set(k, p);
    }
    return folders.get(k)!;
  };
  let done = 0;
  const label = (p: PickedFile) => [...p.path, p.file.name].join('/');
  const one = async (p: PickedFile) => {
    try {
      if (p.file.size > limitBytes) throw new Error(`larger than ${Math.round(limitBytes / 1024 / 1024)} MB`);
      if (p.file.size === 0) throw new Error('empty');
      let target: string;
      try { target = await folderFor(p.path); } catch (e) { throw new Error(`its folder could not be made: ${(e as Error).message}`); }
      await uploadOne(getToken, clientId, target, p.file);
      result.uploaded++;
    } catch (e) {
      result.failed.push({ name: label(p), why: (e as Error).message });
    } finally {
      onProgress(++done, work.length);
    }
  };
  let next = 0;
  const lanes = Array.from({ length: Math.min(3, work.length) }, async () => {
    while (next < work.length) await one(work[next++]!);
  });
  await Promise.all(lanes);
  return result;
}
